<template>
    <Teleport to="body" v-if="mounted">
        <div
            ref="popoverRef"
            class="custom-popover"
            :class="{ 'd-none': !visible }"
            @mouseenter="cancelHide"
            @mouseleave="scheduleHide"
            :data-popper-placement="placement"
        >

            <div class="custom-popover-body">
                <slot></slot>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { createPopper } from '@popperjs/core';

const props = defineProps({
    referenceElement: {
        required: true,
    },
    visible: {
        type: Boolean,
        default: false,
    },
    placement: {
        type: String,
        default: 'bottom',
    },
    offset: {
        type: Array,
        default: () => [0, 8],
    },
});

const emit = defineEmits(['update:visible']);

const popper = ref(null);
const popoverRef = ref(null);
const mounted = ref(false);
let hideTimeout = null;

const scheduleHide = () => {
    hideTimeout = setTimeout(() => {
        emit('update:visible', false);
    }, 100);
};

const cancelHide = () => {
    clearTimeout(hideTimeout);
};

onMounted(() => {
    mounted.value = true;
    nextTick(() => {
        if (!props.referenceElement || !popoverRef.value) return;
        popper.value = createPopper(props.referenceElement, popoverRef.value, {
            placement: props.placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: props.offset,
                    },
                },
            ],
        });
    });
});

onBeforeUnmount(() => {
    if (popper.value) {
        popper.value.destroy();
    }
});

watch(
    () => props.visible,
    (newVal) => {
        if (newVal && popper.value) {
            popper.value.update();
        }
    }
);
</script>

<style scoped>
.custom-popover {
    position: relative;
    display: block;
    max-width: 300px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background-color: #2c2c2c;
    color: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    text-align: center;
    font-size: 0.875rem;
    line-height: 1.4;
    z-index: 9999;
}

.custom-popover-body {
    padding: 1rem;
}

.custom-popover-arrow {
    position: absolute;
    width: 1rem;
    height: 1rem;
    left: 50%;
    transform: translateX(-50%);
}

.custom-popover-arrow::before {
    content: "";
    position: absolute;
    width: 1rem;
    height: 1rem;
    background-color: #2c2c2c;
    transform: rotate(45deg);
}

.custom-popover[data-popper-placement^="bottom"] > .custom-popover-arrow::before {
    top: -0.5rem;
}

.custom-popover[data-popper-placement^="top"] > .custom-popover-arrow::before {
    bottom: -0.5rem;
}

.d-none {
    display: none !important;
}
</style>
